import { getDB } from './direct-couchdb';

const customerService = {
  getCustomerById: async (customerId) => {
    try {
      console.log(`Getting customer with ID: ${customerId}`);
      const customer = await getDB().get(customerId);
      return customer;
    } catch (error) {
      console.error(`Error getting customer with ID ${customerId}:`, error);
      throw new Error('Failed to load customer data');
    }
  },

  createCustomer: async (customer) => {
    try {
      console.log('Creating customer:', customer);
      await getDB().put(customer);
      return customer;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw new Error('Failed to create customer');
    }
  },

  getCustomerAddress: async (customerId) => {
    try {
      const customer = await getDB().get(customerId);
      return customer.address || '';
    } catch (error) {
      console.error(`Error getting customer address:`, error);
      return '';
    }
  },

  // Multiple address management
  addCustomerAddress: async (customerId, addressData) => {
    try {
      const customer = await getDB().get(customerId);
      
      if (!customer.addresses) {
        customer.addresses = [];
      }
      
      const newAddress = {
        id: Date.now().toString(),
        ...addressData,
        createdAt: new Date().toISOString()
      };
      
      // If this is the first address or marked as default, make it default
      if (customer.addresses.length === 0 || addressData.isDefault) {
        // Remove default from other addresses
        customer.addresses.forEach(addr => addr.isDefault = false);
        newAddress.isDefault = true;
      }
      
      customer.addresses.push(newAddress);
      customer.updatedAt = new Date().toISOString();
      
      await getDB().put(customer);
      return newAddress;
    } catch (error) {
      console.error('Error adding customer address:', error);
      throw error;
    }
  },

  updateCustomerAddress: async (customerId, addressId, addressData) => {
    try {
      const customer = await getDB().get(customerId);
      
      if (!customer.addresses) {
        throw new Error('No addresses found');
      }
      
      const addressIndex = customer.addresses.findIndex(addr => addr.id === addressId);
      if (addressIndex === -1) {
        throw new Error('Address not found');
      }
      
      // If setting as default, remove default from others
      if (addressData.isDefault) {
        customer.addresses.forEach(addr => addr.isDefault = false);
      }
      
      customer.addresses[addressIndex] = {
        ...customer.addresses[addressIndex],
        ...addressData,
        updatedAt: new Date().toISOString()
      };
      
      customer.updatedAt = new Date().toISOString();
      
      await getDB().put(customer);
      return customer.addresses[addressIndex];
    } catch (error) {
      console.error('Error updating customer address:', error);
      throw error;
    }
  },

  deleteCustomerAddress: async (customerId, addressId) => {
    try {
      const customer = await getDB().get(customerId);
      
      if (!customer.addresses) {
        throw new Error('No addresses found');
      }
      
      const addressIndex = customer.addresses.findIndex(addr => addr.id === addressId);
      if (addressIndex === -1) {
        throw new Error('Address not found');
      }
      
      const wasDefault = customer.addresses[addressIndex].isDefault;
      customer.addresses.splice(addressIndex, 1);
      
      // If deleted address was default, make first remaining address default
      if (wasDefault && customer.addresses.length > 0) {
        customer.addresses[0].isDefault = true;
      }
      
      customer.updatedAt = new Date().toISOString();
      
      await getDB().put(customer);
      return true;
    } catch (error) {
      console.error('Error deleting customer address:', error);
      throw error;
    }
  },

  getCustomerAddresses: async (customerId) => {
    try {
      const customer = await getDB().get(customerId);
      return customer.addresses || [];
    } catch (error) {
      console.error('Error getting customer addresses:', error);
      return [];
    }
  },

  getDefaultAddress: async (customerId) => {
    try {
      const customer = await getDB().get(customerId);
      if (!customer.addresses) return null;
      
      return customer.addresses.find(addr => addr.isDefault) || customer.addresses[0] || null;
    } catch (error) {
      console.error('Error getting default address:', error);
      return null;
    }
  },

  setDefaultAddress: async (customerId, addressId) => {
    try {
      const customer = await getDB().get(customerId);
      
      if (!customer.addresses) {
        throw new Error('No addresses found');
      }
      
      // Remove default from all addresses
      customer.addresses.forEach(addr => addr.isDefault = false);
      
      // Set new default
      const addressIndex = customer.addresses.findIndex(addr => addr.id === addressId);
      if (addressIndex === -1) {
        throw new Error('Address not found');
      }
      
      customer.addresses[addressIndex].isDefault = true;
      customer.updatedAt = new Date().toISOString();
      
      await getDB().put(customer);
      return customer.addresses[addressIndex];
    } catch (error) {
      console.error('Error setting default address:', error);
      throw error;
    }
  }
};

export default customerService;